(ns ui.view.list-opensearch.view
  (:require
   [clojure.set :as set]
   [common.localization :as loc]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.virtuoso :as v]
   [ui.dom-globals :as dg]
   [ui.view.list-opensearch.event :as event]
   [ui.view.list-opensearch.sub :as sub]
   [ui.view.opensearch-cards.view :as cards]))

(defn- item-id
  [card]
  (or (get card "original.db/id")
      (get card :original.db/id)
      (:db/id card)
      (:id card)))

(defn- card-placeholder
  []
  [:div
   {:class "tw2-rounded-3xl tw2-border tw2-border-dashed tw2-border-slate-200 tw2-bg-slate-50 tw2-p-6 tw2-text-sm tw2-text-slate-400"}
   "Loading..."])

(defn- maybe-load-more!
  [{:keys [list-id can-load-more? next-offset]}]
  (when can-load-more?
    (rf/dispatch [::event/load-page {:list-id list-id
                                     :start-index next-offset
                                     :count 50}])))

(defn- maybe-load-group-more!
  [{:keys [list-id expanded-groups]}]
  (rf/dispatch [::event/load-next-group-page {:list-id list-id
                                              :expanded-groups expanded-groups}]))

(defn- near-bottom?
  [e]
  (let [node (dg/current-target e)
        scroll-top (dg/scroll-top node)
        client-height (dg/client-height node)
        scroll-height (dg/scroll-height node)]
    (>= (+ scroll-top client-height 160)
        scroll-height)))

(defn- collapse-state
  [items collapsed-ids]
  (let [ids (set (keep item-id items))
        collapsed-visible-ids (set/intersection ids (set (or collapsed-ids #{})))]
    (cond
      (empty? ids) :all-expanded
      (empty? collapsed-visible-ids) :all-expanded
      (= (count ids) (count collapsed-visible-ids)) :all-collapsed
      :else :mixed)))

(defn- report-collapse-state!
  [all-visible-items ui-state on-collapse-state-changed]
  (when (fn? on-collapse-state-changed)
    (on-collapse-state-changed
     (collapse-state all-visible-items (:collapsed-ids ui-state)))))

(defn- collapse-state-with-group-collapses
  [all-visible-items ui-state group-items-by-value]
  (let [collapsed-item-ids (into (set (or (:collapsed-ids ui-state) #{}))
                                 (mapcat (fn [group-value]
                                           (map item-id (get group-items-by-value group-value [])))
                                         (or (:collapsed-groups ui-state) #{})))]
    (collapse-state all-visible-items collapsed-item-ids)))

(defn- toggle-collapsed!
  [ui-state* card]
  (when-let [id (item-id card)]
    (swap! ui-state*
           update
           :collapsed-ids
           (fn [collapsed-ids]
             (let [collapsed-ids (or collapsed-ids #{})]
               (if (contains? collapsed-ids id)
                 (disj collapsed-ids id)
                 (conj collapsed-ids id)))))))

(defn- card-props
  [{:keys [ui-state* card card-config full-width? compact? width on-card-clicked on-selection-changed
           all-visible-items on-collapse-state-changed]}]
  (let [state @ui-state*
        id (item-id card)]
    {:card card
     :card-config card-config
     :full-width? full-width?
     :compact? compact?
     :width width
     :selected? (contains? (:selected-ids state #{}) id)
     :collapsed? (contains? (:collapsed-ids state #{}) id)
     :on-click (fn [e clicked-card]
                 (cards/select-card! ui-state* clicked-card all-visible-items on-selection-changed e))
     :on-double-click (fn [clicked-card]
                        (when (fn? on-card-clicked)
                          (on-card-clicked clicked-card)))
     :on-toggle-collapse (fn [clicked-card]
                           (let [next-state (toggle-collapsed! ui-state* clicked-card)]
                             (report-collapse-state! all-visible-items next-state on-collapse-state-changed)))}))

(defn- gallery-body
  [{:keys [items card-config list-id can-load-more? next-offset on-card-clicked on-selection-changed
           ui-state* all-visible-items on-collapse-state-changed]}]
  (let [card-elements
        (mapv (fn [index card]
                ^{:key (str "gallery-card-" index "-" (item-id card))}
                [:div {:class "shrink-0"
                       :style {:width "280px"
                               :max-width "calc(50vw - 2rem)"}}
                 [cards/render-card
                  (card-props {:ui-state* ui-state*
                               :items items
                               :index index
                               :card card
                               :card-config card-config
                               :compact? true
                               :width "280px"
                               :on-card-clicked on-card-clicked
                               :on-selection-changed on-selection-changed
                               :all-visible-items all-visible-items
                               :on-collapse-state-changed on-collapse-state-changed})]])
              (range (count items))
              items)
        gallery-children (cond-> (vec card-elements)
                           can-load-more?
                           (conj [:div {:class "w-full py-2"}
                                  [card-placeholder]]))]
    [:div {:class "flex-1 min-h-0 overflow-auto px-4 pb-4"
           :on-scroll (fn [e]
                        (when (near-bottom? e)
                          (maybe-load-more! {:list-id list-id
                                             :can-load-more? can-load-more?
                                             :next-offset next-offset})))}
     (into [:div {:class "flex flex-wrap content-start gap-4"}]
           gallery-children)]))

(defn- group-header
  [{:keys [label count collapsed? on-toggle]}]
  [:button
   {:type "button"
    :class "tw2-mb-3 tw2-flex tw2-w-full tw2-items-center tw2-justify-between tw2-rounded-[14px] tw2-border tw2-border-slate-200 tw2-bg-slate-50 tw2-px-3 tw2-py-2 hover:tw2-bg-slate-100"
    :on-click on-toggle}
   [:div {:class "tw2-flex tw2-items-center tw2-gap-3"}
    [:span {:class "tw2-text-sm tw2-font-semibold tw2-text-slate-900"}
     (or label (pr-str label))]
    [:span {:class "tw2-text-xs tw2-text-slate-500"}
     (loc/tr-item-count count)]]
   [:span {:class "tw2-text-slate-500"}
    [:i.fal.fa-chevron-down
     {:class (str "tw2-transition-transform " (when collapsed? "-tw2-rotate-90"))}]]])

(defn- toggle-collapsed-group
  [ui-state* group-value]
  (swap! ui-state* update :collapsed-groups
         (fn [xs]
           (let [xs (or xs #{})]
             (if (contains? xs group-value)
               (disj xs group-value)
               (conj xs group-value))))))

(defn- grouped-list-body
  [{:keys [group-order groups-by-value list-id ui-state* card-config on-card-clicked on-selection-changed
           all-visible-items on-collapse-state-changed]}]
  (let [collapsed-groups (or (:collapsed-groups @ui-state*) #{})
        group-items-by-value (into {}
                                   (for [group-value group-order
                                         :let [group-state @(rf/subscribe [::sub/group-state list-id group-value])]]
                                     [group-value (vec (or (:items group-state) []))]))]
    [:div {:class "flex-1 min-h-0 overflow-auto px-4 pb-4"
           :on-scroll (fn [e]
                        (when (near-bottom? e)
                          (maybe-load-group-more! {:list-id list-id
                                                   :expanded-groups (remove collapsed-groups group-order)})))}
     (doall
      (for [group-value group-order]
        (let [group-meta (get groups-by-value group-value)
              group-state @(rf/subscribe [::sub/group-state list-id group-value])
              items (vec (or (:items group-state) []))
              collapsed? (contains? collapsed-groups group-value)
              has-more? (true? (:has-more? group-state))
              status (:status group-state)
              next-offset (:next-offset group-state)]
          ^{:key (str "group-list-" (pr-str group-value))}
           [:div {:class "tw2-mb-2"}
           [group-header {:label (:label group-meta)
                          :count (or (:count group-meta) (:total group-state) 0)
                          :collapsed? collapsed?
                          :on-toggle (fn [_]
                                       (let [next-state (toggle-collapsed-group ui-state* group-value)]
                                         (when (fn? on-collapse-state-changed)
                                           (on-collapse-state-changed
                                            (collapse-state-with-group-collapses all-visible-items
                                                                                 next-state
                                                                                 group-items-by-value))))) }]
           (when-not collapsed?
             [:div
              (doall
               (for [[index card] (map-indexed vector items)]
                 ^{:key (str "group-card-" (pr-str group-value) "-" index "-" (item-id card))}
                 [:div {:class "w-full pb-3"}
                  [cards/render-card
                   (card-props {:ui-state* ui-state*
                                :items items
                                :index index
                                :card card
                                :card-config card-config
                                :full-width? true
                                :on-card-clicked on-card-clicked
                                :on-selection-changed on-selection-changed
                                :all-visible-items all-visible-items
                                :on-collapse-state-changed on-collapse-state-changed})]]))
              (when (and has-more?
                         (not= status :loading)
                         (some? next-offset))
                [:div {:class "w-full py-2"}
                 [card-placeholder]])])])))]))

(defn- grouped-gallery-body
  [{:keys [group-order groups-by-value list-id ui-state* card-config on-card-clicked on-selection-changed
           all-visible-items on-collapse-state-changed]}]
  (let [collapsed-groups (or (:collapsed-groups @ui-state*) #{})
        group-items-by-value (into {}
                                   (for [group-value group-order
                                         :let [group-state @(rf/subscribe [::sub/group-state list-id group-value])]]
                                     [group-value (vec (or (:items group-state) []))]))]
    [:div {:class "flex-1 min-h-0 overflow-auto px-4 pb-4"
           :on-scroll (fn [e]
                        (when (near-bottom? e)
                          (maybe-load-group-more! {:list-id list-id
                                                   :expanded-groups (remove collapsed-groups group-order)})))}
     (doall
      (for [group-value group-order]
        (let [group-meta (get groups-by-value group-value)
              group-state @(rf/subscribe [::sub/group-state list-id group-value])
              items (vec (or (:items group-state) []))
              collapsed? (contains? collapsed-groups group-value)
              has-more? (true? (:has-more? group-state))
              status (:status group-state)
              next-offset (:next-offset group-state)]
          ^{:key (str "group-gallery-" (pr-str group-value))}
           [:div {:class "tw2-mb-2"}
           [group-header {:label (:label group-meta)
                          :count (or (:count group-meta) (:total group-state) 0)
                          :collapsed? collapsed?
                          :on-toggle (fn [_]
                                       (let [next-state (toggle-collapsed-group ui-state* group-value)]
                                         (when (fn? on-collapse-state-changed)
                                           (on-collapse-state-changed
                                            (collapse-state-with-group-collapses all-visible-items
                                                                                 next-state
                                                                                 group-items-by-value))))) }]
           (when-not collapsed?
             [:div
              (into [:div {:class "flex flex-wrap content-start gap-4"}]
                    (doall
                     (for [[index card] (map-indexed vector items)]
                       ^{:key (str "group-gallery-card-" (pr-str group-value) "-" index "-" (item-id card))}
                       [:div {:class "shrink-0"
                              :style {:width "280px"
                                      :max-width "calc(50vw - 2rem)"}}
                        [cards/render-card
                         (card-props {:ui-state* ui-state*
                                      :items items
                                      :index index
                                      :card card
                                      :card-config card-config
                                      :compact? true
                                      :width "280px"
                                      :on-card-clicked on-card-clicked
                                      :on-selection-changed on-selection-changed
                                      :all-visible-items all-visible-items
                                      :on-collapse-state-changed on-collapse-state-changed})]])))
              (when (and has-more?
                         (not= status :loading)
                         (some? next-offset))
                [:div {:class "w-full py-2"}
                 [card-placeholder]])])])))]))

(defn- list-body
  [{:keys [items card-config list-id can-load-more? next-offset loaded virtuoso-total on-card-clicked on-selection-changed
           ui-state* all-visible-items on-collapse-state-changed]}]
  [:div {:class "flex-1 min-h-0 px-4 pb-4"}
   [v/virtuoso
    {:style {:height "100%"}
     :totalCount virtuoso-total
     :overscan 6
     :itemContent (fn [index]
                    (let [card (get items index)
                          load-more-placeholder? (and can-load-more?
                                                      (= index loaded))]
                      (r/as-element
                       (cond
                         card
                         ^{:key (str "list-card-" index "-" (item-id card))}
                         [:div {:class "w-full pb-3"}
                          [cards/render-card
                           (card-props {:ui-state* ui-state*
                                        :items items
                                        :index index
                                        :card card
                                        :card-config card-config
                                        :full-width? true
                                        :on-card-clicked on-card-clicked
                                        :on-selection-changed on-selection-changed
                                        :all-visible-items all-visible-items
                                        :on-collapse-state-changed on-collapse-state-changed})]]

                         load-more-placeholder?
                         ^{:key (str "list-load-more-" index)}
                         [card-placeholder]

                         :else
                         ^{:key (str "list-empty-" index)}
                         [:div {:style {:height "1px"}}]))))
     :endReached (fn [_]
                   (maybe-load-more! {:list-id list-id
                                      :can-load-more? can-load-more?
                                      :next-offset next-offset}))}]])

(defn view
  [{:keys [list-id] :as props}]
  (let [ui-state* (r/atom {:selected-ids #{}
                           :collapsed-ids #{}
                           :last-selected-index nil})
        last-collapse-command-id* (r/atom nil)]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (rf/dispatch [::event/init props]))

      :component-did-update
      (fn [this old-argv]
        (let [old-props (second old-argv)
              new-props (r/props this)
              old-list-id (:list-id old-props)
              new-list-id (:list-id new-props)
              old-layout (:layout old-props)
              new-layout (:layout new-props)
              old-config (:config old-props)
              new-config (:config new-props)]
          (when (not= old-list-id new-list-id)
            (reset! last-collapse-command-id* nil)
            (rf/dispatch [::event/destroy {:list-id old-list-id}]))
          (when (or (not= old-list-id new-list-id)
                    (not= old-layout new-layout)
                    (not= old-config new-config))
            (reset! ui-state* {:selected-ids #{}
                               :collapsed-ids #{}
                               :last-selected-index nil})
            (rf/dispatch [::event/init new-props]))))

      :component-will-unmount
      (fn [_]
        (rf/dispatch [::event/destroy {:list-id list-id}]))

      :reagent-render
      (fn [{:keys [list-id layout collapse-command on-card-clicked on-selection-changed on-collapse-state-changed]}]
        (let [items @(rf/subscribe [::sub/items list-id])
              grouped? @(rf/subscribe [::sub/grouped? list-id])
              group-order @(rf/subscribe [::sub/group-order list-id])
              groups-by-value @(rf/subscribe [::sub/group-by-value list-id])
              status @(rf/subscribe [::sub/status list-id])
              total @(rf/subscribe [::sub/total list-id])
              next-offset @(rf/subscribe [::sub/next-offset list-id])
              has-more? @(rf/subscribe [::sub/has-more? list-id])
              error @(rf/subscribe [::sub/error list-id])
              card-config @(rf/subscribe [::sub/card-config list-id])
              initialized? (and (some? card-config)
                                (not= status :uninitialized))
              gallery? (= layout :gallery)
              loaded (count items)
              can-load-more? (and initialized?
                                  has-more?
                                  (not= status :loading))
              virtuoso-total (if can-load-more?
                               (inc loaded)
                               loaded)
              empty-state? (and initialized?
                                (not= status :loading)
                                (zero? total))
              all-visible-items (if grouped?
                                  (->> group-order
                                       (mapcat (fn [group-value]
                                                 (or (:items @(rf/subscribe [::sub/group-state list-id group-value]))
                                                     [])))
                                       vec)
                                  (vec items))
              collapse-command-id (:id collapse-command)]
          (when (and collapse-command-id
                     (not= collapse-command-id @last-collapse-command-id*))
            (reset! last-collapse-command-id* collapse-command-id)
            (case (:action collapse-command)
              :collapse-all
              (swap! ui-state* assoc :collapsed-ids (into #{} (keep item-id all-visible-items)))

              :expand-all
              (swap! ui-state* assoc
                     :collapsed-ids #{}
                     :collapsed-groups #{})

              ;; The listing cleared its selection. The cards that the view shows
              ;; as selected are in its own state.
              :clear-selection
              (swap! ui-state* assoc
                     :selected-ids #{}
                     :last-selected-index nil)

              nil))
          [:div {:class "flex h-full min-h-0 flex-col"}
           (when error
             [:div {:class "px-4 py-2 text-sm text-red-600"}
              (str "Failed to load list: " (pr-str error))])
           [:div {:class "px-4 py-2 text-xs opacity-70"}
            (str total " items")]
           (if-not initialized?
             [:div {:class "px-4 py-6 text-sm text-base-light-text-paler"}
              "Loading items..."]
             (cond
               empty-state?
               [:div {:class "flex flex-1 items-center justify-center px-4 py-8 text-sm text-base-light-text-paler"}
                (loc/tr :grid-no-entities-found)]

               (and grouped? gallery?)
               [grouped-gallery-body {:group-order group-order
                                      :groups-by-value groups-by-value
                                      :list-id list-id
                                      :card-config card-config
                                      :on-card-clicked on-card-clicked
                                      :on-selection-changed on-selection-changed
                                      :all-visible-items all-visible-items
                                      :on-collapse-state-changed on-collapse-state-changed
                                      :ui-state* ui-state*}]

               grouped?
               [grouped-list-body {:group-order group-order
                                   :groups-by-value groups-by-value
                                   :list-id list-id
                                   :card-config card-config
                                   :on-card-clicked on-card-clicked
                                   :on-selection-changed on-selection-changed
                                   :all-visible-items all-visible-items
                                   :on-collapse-state-changed on-collapse-state-changed
                                   :ui-state* ui-state*}]

               gallery?
               [gallery-body {:items items
                              :card-config card-config
                              :list-id list-id
                              :can-load-more? can-load-more?
                              :next-offset next-offset
                              :on-card-clicked on-card-clicked
                              :on-selection-changed on-selection-changed
                              :all-visible-items all-visible-items
                              :on-collapse-state-changed on-collapse-state-changed
                              :ui-state* ui-state*}]

               :else
               [list-body {:items items
                           :card-config card-config
                           :list-id list-id
                           :can-load-more? can-load-more?
                           :next-offset next-offset
                           :loaded loaded
                           :virtuoso-total virtuoso-total
                           :on-card-clicked on-card-clicked
                           :on-selection-changed on-selection-changed
                           :all-visible-items all-visible-items
                           :on-collapse-state-changed on-collapse-state-changed
                           :ui-state* ui-state*}]))]))})))
